import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { Observable, Subject, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AppConstants } from '../../commons/app-constants';

export type FeedbackRating = 'SAD' | 'NEUTRAL' | 'HAPPY' | 'LOVE';
export type FeedbackStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface AppFeedbackRequest {
  rating: FeedbackRating;
  highlightText?: string | null;
  issueText?: string | null;
  showName: boolean;
  showCompany: boolean;
}

export interface FeedbackPromptStatus {
  showFeedbackPrompt: boolean;
  requestStoreReview: boolean;
}

export interface AdminFeedbackRow {
  id: number;
  rating: FeedbackRating;
  highlightText: string | null;
  issueText: string | null;
  name: string | null;
  publicName: string | null;
  firmName: string | null;
  phone: string | null;
  showName: boolean;
  showCompany: boolean;
  approvalStatus: FeedbackStatus;
  approvedAt: string | null;
  source: 'PUBLIC_FORM' | 'IN_APP';
  createdAt: string;
}

export interface AdminFeedbackPage {
  content: AdminFeedbackRow[];
  totalElements: number;
}

/** Registered by name instead of importing '@capawesome/capacitor-app-review' so the WEB build
 *  never depends on that package — only the native iOS/Android builds need it installed. */
const AppReview = registerPlugin<{ requestReview(): Promise<void> }>('AppReview');

@Injectable({ providedIn: 'root' })
export class FeedbackService {
  private readonly base = `${AppConstants.API_URL}api/v1/feedback`;
  private readonly adminBase = `${AppConstants.API_URL}api/v1/admin/feedback`;

  /** Opens the shared Give Feedback dialog from anywhere (avatar menu, side menu, prompt card). */
  readonly openDialog$ = new Subject<void>();

  constructor(private http: HttpClient) {}

  openDialog(): void {
    this.openDialog$.next();
  }

  submit(req: AppFeedbackRequest): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(this.base, req);
  }

  promptStatus(): Observable<FeedbackPromptStatus> {
    return this.http.get<FeedbackPromptStatus>(`${this.base}/prompt-status`).pipe(
      catchError(() => of({ showFeedbackPrompt: false, requestStoreReview: false })),
    );
  }

  markPromptDismissed(): Observable<unknown> {
    return this.http.post(`${this.base}/prompt-dismissed`, {}).pipe(catchError(() => of(null)));
  }

  /**
   * Native only. Apple/Google decide whether the popup actually appears (Apple: max 3 a year),
   * and we never ask a question first — asking for an opinion before this, or sending only happy
   * users here, breaks both stores' review rules.
   */
  async requestStoreReview(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    try {
      await AppReview.requestReview();
    } catch {
      // Plugin missing on an older native build, or the OS declined — nothing to do.
    }
    this.http.post(`${this.base}/store-review-requested`, {}).pipe(catchError(() => of(null))).subscribe();
  }

  // ─── Admin (SUPER_ADMIN / ROOT) ───

  adminList(status: FeedbackStatus, page: number, size: number): Observable<AdminFeedbackPage> {
    return this.http.get<AdminFeedbackPage>(this.adminBase, { params: { status, page, size } });
  }

  adminCounts(): Observable<Record<FeedbackStatus, number>> {
    return this.http.get<Record<FeedbackStatus, number>>(`${this.adminBase}/counts`);
  }

  setStatus(id: number, action: 'approve' | 'reject' | 'reset'): Observable<AdminFeedbackRow> {
    return this.http.post<AdminFeedbackRow>(`${this.adminBase}/${id}/${action}`, {});
  }
}
